import { MapPin, Navigation, Route, Star, SortAsc, ArrowUpDown, Compass } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Sort modes
export type SortMode = 'name' | 'fromMe' | 'fromLast' | 'shortest' | 'class';

// Priority modes (mutually exclusive)
export type PriorityMode = 'none' | 'priority' | 'priorityGps';

// Check if sort mode is GPS-based (route optimization)
export function isGpsMode(mode: SortMode): boolean {
  return mode === 'fromMe' || mode === 'fromLast' || mode === 'shortest';
}

const sortOptions: { value: SortMode; label: string; icon: React.ElementType; isGps: boolean }[] = [
  { value: 'name', label: 'Алфавит (А-Я)', icon: SortAsc, isGps: false },
  { value: 'class', label: 'Класс клиента', icon: Star, isGps: false },
  { value: 'fromMe', label: 'От текущей точки', icon: MapPin, isGps: true },
  { value: 'fromLast', label: 'От последнего визита', icon: Navigation, isGps: true },
  { value: 'shortest', label: 'Кратчайший маршрут', icon: Route, isGps: true },
];

// Priority options for non-GPS modes
const simplePriorityOptions: { value: PriorityMode; label: string; hint?: string }[] = [
  { value: 'none', label: 'Без приоритета' },
  { value: 'priority', label: 'С приоритетом' },
];

// Priority options for GPS modes
const gpsPriorityOptions: { value: PriorityMode; label: string; hint?: string }[] = [
  { value: 'none', label: 'Без приоритета' },
  { value: 'priority', label: 'Группировка', hint: 'сначала срочные' },
  { value: 'priorityGps', label: 'Группировка + GPS', hint: 'оптимально в каждой группе' },
];

interface SortMenuProps {
  value: SortMode;
  onChange: (value: SortMode) => void;
  priorityMode: PriorityMode;
  onPriorityModeChange: (value: PriorityMode) => void;
}

export function SortMenu({ value, onChange, priorityMode, onPriorityModeChange }: SortMenuProps) {
  const currentOption = sortOptions.find((opt) => opt.value === value);
  const isGps = isGpsMode(value);
  const priorityOptions = isGps ? gpsPriorityOptions : simplePriorityOptions;

  // Handle sort mode change - adjust priority if needed
  const handleSortChange = (newSort: SortMode) => {
    onChange(newSort);

    // If switching from GPS to non-GPS mode and priorityGps is selected,
    // downgrade to regular priority
    if (!isGpsMode(newSort) && priorityMode === 'priorityGps') {
      onPriorityModeChange('priority');
    }
  };

  // Icon for button
  const getButtonIcon = () => {
    if (priorityMode === 'priorityGps') return Compass;
    if (priorityMode === 'priority') return ArrowUpDown;
    return currentOption?.icon ?? SortAsc;
  };

  const ButtonIcon = getButtonIcon();

  // Compact label for button
  const getButtonLabel = () => {
    const shortLabels: Record<SortMode, string> = {
      name: 'А-Я',
      class: 'Класс',
      fromMe: 'Текущ.',
      fromLast: 'Посл.',
      shortest: 'Кратч.',
    };
    const sortLabel = shortLabels[value] ?? 'Сорт.';
    if (priorityMode === 'priorityGps') return `${sortLabel}+GPS`;
    if (priorityMode === 'priority') return `${sortLabel}+П`;
    return sortLabel;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1 px-2">
          <ButtonIcon className="h-4 w-4" />
          <span className="text-xs">
            {getButtonLabel()}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Sort mode selection - unified block */}
        <DropdownMenuLabel>Сортировка</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => handleSortChange(v as SortMode)}
        >
          {sortOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              className="gap-2"
              onSelect={(e) => e.preventDefault()}
            >
              <option.icon className="h-4 w-4 shrink-0" />
              <span>{option.label}</span>
              {option.isGps && (
                <span className="ml-auto text-xs text-muted-foreground">GPS</span>
              )}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        {/* Priority mode selection - options depend on sort mode */}
        <DropdownMenuLabel>
          Приоритет {isGps && <span className="text-muted-foreground font-normal">(для маршрута)</span>}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={priorityMode}
          onValueChange={(v) => onPriorityModeChange(v as PriorityMode)}
        >
          {priorityOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              className="flex-col items-start gap-0"
              onSelect={(e) => e.preventDefault()}
            >
              <span>{option.label}</span>
              {'hint' in option && option.hint && (
                <span className="text-xs text-muted-foreground">{option.hint}</span>
              )}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
